import React from 'react';
import cn from 'classnames';
import { generatePath, NavLink, useHistory } from 'react-router-dom';

import { categories as categoriesApi } from 'client/redux/api/categories';
import Loader from '../Loader';
import { useTranslation, useAppDispatch, useAppSelector } from 'client/hooks';
import { setMenuState, setPopup } from 'client/redux/ui';
import { selectUI } from 'client/redux/ui/selectors';
import { selectAuth } from 'client/redux/user/selectors';
import { selectLang } from 'client/redux/settings/selectors';

import { USER_PROFILE_ROUTE, CATEGORY_PAGE_ROUTE, INVESTMENT_OPPORTUNITIES } from 'client/constants/routes';
import { selectUserRole } from 'client/redux/user/selectors';

import { getMenuLinks } from './helpers';

import css from './Menu.module.scss';

const Menu = () => {
  const { translate } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { bannerJoin } = useAppSelector(selectUI);
  const auth = useAppSelector(selectAuth);
  const lang = useAppSelector(selectLang);
  const role = useAppSelector(selectUserRole);

  const menuLinks = getMenuLinks(lang, true);

  const onMyProfileClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (!auth.isLoggedIn) {
      dispatch(setPopup({ type: 'auth', data: { initialAuthType: 'sign-in' } }));
    } else {
      history.push(generatePath(USER_PROFILE_ROUTE, { lang }));
    }

    clickOnLink();
  };

  const onInvestmentOppClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const link = generatePath(INVESTMENT_OPPORTUNITIES, { lang });

    if (!auth.isLoggedIn) {
      dispatch(
        setPopup({ type: 'auth', data: { initialAuthType: 'sign-in', redirectUrl: link, forceRedirect: true } })
      );
    } else if (role === 'VIEWER' && auth.isLoggedIn) {
      dispatch(setPopup({ type: 'join-as-investor', data: { link: link, isExternalLink: true } }));
    } else {
      history.push(link);
    }

    clickOnLink();
  };

  const clickOnLink = () => {
    dispatch(setMenuState('hidden'));
  };

  const { data, isLoading } = categoriesApi.endpoints.getAllCategories.useQuery({ isVisible: true });
  const categories = data?.success ? data.data : [];

  return (
    <div className={cn(css.menuWrapper, !auth.isLoggedIn && bannerJoin === 'visible' && css.withOpenedBanner)}>
      <div className={css.filter}></div>
      <div className={css.content}>
        <div className={css.menu}>
          <div className={css.title}>{translate('menu.titles.menu')}</div>
          <div className={css.links}>
            {menuLinks.map((link) => {
              if (link.name === 'news')
                return (
                  <a href={link.link} key={`${link.link}`} className={css.navlink} target="_blank" rel="noreferrer">
                    {translate(`menu.menuItems.${link.name}`)}
                  </a>
                );

              if (link.name === 'myProfile')
                return (
                  <a href="#" key={`${link.link}`} onClick={onMyProfileClick} className={css.navlink}>
                    {translate(`menu.menuItems.${link.name}`)}
                  </a>
                );

              if (link.name === 'investmentOpportunities')
                return (
                  <a href="#" key={`${link.link}`} onClick={onInvestmentOppClick} className={css.navlink}>
                    {translate(`menu.menuItems.${link.name}`)}
                  </a>
                );
              return (
                <NavLink
                  to={link.link}
                  key={`${link.link}`}
                  className={(isActive) => {
                    return cn(css.navlink, isActive && css.activeLink);
                  }}
                  onClick={clickOnLink}
                >
                  {translate(`menu.menuItems.${link.name}`)}
                </NavLink>
              );
            })}
          </div>
        </div>
        <div className={css.categories}>
          <div className={css.title}>{translate('menu.titles.categories')}</div>

          <div className={css.links}>
            {isLoading && <Loader position="relative" className={css.loader} />}
            {categories &&
              categories.map((category) => {
                return (
                  <NavLink
                    to={generatePath(CATEGORY_PAGE_ROUTE, { id: category.slug, lang })}
                    key={`${category._id.toString()}`}
                    className={(isActive) => {
                      return cn(css.navlink, isActive && css.activeLink);
                    }}
                    onClick={clickOnLink}
                  >
                    {category.name}
                  </NavLink>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Menu;
